// src/modules/app-views/toolbox/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { Tabs } from 'antd';
import translateX from 'utils/translateX';
import { useEffect, useMemo } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import { FormOutlined, SettingOutlined, DollarOutlined, LinkOutlined } from '@ant-design/icons';
import { RiRobot2Line } from 'react-icons/ri';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import ToolboxRecordTab from 'modules/app-views/toolbox/pages/Document/tabs/RecordTab';
import ToolboxSpecsTab from 'modules/app-views/toolbox/pages/Document/tabs/SpecsTab';
import ToolboxManagementTab from 'modules/app-views/toolbox/pages/Document/tabs/ManagementTab';
import ToolboxReferencesTab from 'modules/app-views/toolbox/pages/Document/tabs/ReferencesTab';
import ToolboxAiInfoTab from 'modules/app-views/toolbox/pages/Document/tabs/AiInfoTab';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ToolboxDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode } =
    useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // Get upsert from global state - auto-fetch is handled by DocumentProvider
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef } = useFormInstance({ data });
  const _id = urlInfo?.params?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const requiredFields = ['name'];

  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // NOTE: useMemo instead of useState to react when data becomes available
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Build tabs
  useEffect(() => {
    const childProps = {
      ...props,
      initTab,
      viewMode,
      data,
    };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [initTab, viewMode, data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      const toolId = values?._id || allValues?._id || data?._id;
      const dataToSave = { ...values };

      await upsert({ dataSet: dataToSave, _id: toolId });
    } catch (err) {
      throw err;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus="name"
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={requiredFields}
          locked={viewMode === 'view'}
          showInfoMessage={true}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={{ position: 'relative' }}>
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.name}
              watchField="countryCode"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ToolboxDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, viewMode, data } = props;

  const isNewDocument = viewMode === 'add';
  const hasCatalogedByAi = data?.aiCatalogMeta?.catalogedByAI === true;

  return [
    {
      key: initTab,
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FormOutlined />
          {translateX('record')}
        </span>
      ),
      hide: false,
      disabled: false,
      children: <ToolboxRecordTab {...props} tabName={initTab} />,
    },
    {
      key: 'specs',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <SettingOutlined />
          {translateX('specifications')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <ToolboxSpecsTab {...props} tabName="specs" />,
    },
    {
      key: 'management',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <DollarOutlined />
          {translateX('management')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <ToolboxManagementTab {...props} tabName="management" />,
    },
    {
      key: 'references',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <LinkOutlined />
          {translateX('references')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <ToolboxReferencesTab {...props} tabName="references" />,
    },
    {
      key: 'aiInfo',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <RiRobot2Line />
          {translateX('ai_cataloging')}
        </span>
      ),
      hide: !hasCatalogedByAi,
      disabled: false,
      children: <ToolboxAiInfoTab {...props} tabName="aiInfo" />,
    },
  ];
}
